import React, { useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  Grid,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import { Info, Refresh } from '@mui/icons-material';
import DriverStatusChip from './DriverStatusChip';

const DriverListActionBar = ({
  searchTerm,
  onSearchChange,
  statusCounts,
  totalDrivers,
  onStatusClick,
  selectedStatus,
  onDriversUpdate,
}) => {
  // Define all possible statuses
  const allStatuses = [0, 10, 15, 30, 50, 'unknown'];
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const statusExplanations = {
    0: {
      name: 'New',
      code: 0,
      meaning: 'The driver has successfully logged into Sonda for the first time, but no pictures have been collected yet.',
      when: 'After the driver\'s first login to Sonda',
      action: 'None - the system will automatically start collecting pictures',
    },
    10: {
      name: 'Registering',
      code: 10,
      meaning: 'The system has started collecting pictures of the driver, but fewer than 15 pictures have been captured.',
      when: 'After the driver logs in and the picture collection process begins',
      action: 'None - wait until 15 pictures are collected',
    },
    15: {
      name: 'Pending to Review',
      code: 15,
      meaning: 'The system has collected 15 pictures for the driver, and they are ready for review.',
      when: 'Once exactly 15 pictures have been collected',
      action: 'Review the pictures and mark them as valid or not valid',
    },
    30: {
      name: 'Valid',
      code: 30,
      meaning: 'The driver has at least 5 valid pictures approved by a reviewer.',
      when: 'After a reviewer has marked at least 5 pictures as valid',
      action: 'None - driver registration is complete',
    },
    50: {
      name: 'ID Verification Alert',
      code: 50,
      meaning: 'The driver identified does not match with the identity of the driver that should be driving. ID verification alert has been triggered.',
      when: 'Automatically assigned when pictures are marked as "ID alert" during the verification process',
      action: 'Review the driver\'s identification documents and pictures. Verify identity manually or request updated documentation.',
    },
    'unknown': {
      name: 'Unknown',
      code: 'N/A',
      meaning: 'The driver exists in the system but has not been registered for face identification yet.',
      when: 'Driver has not logged into the face ID system or no face ID status has been assigned',
      action: 'Wait for the driver to log in and start the face ID registration process, or manually initiate registration.',
    },
  };

  return (
    <Box sx={{ mb: 1 }}>
      {/* First row: Search and Total */}
      <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Grid item xs={8}>
          <TextField
            fullWidth
            size="small"
            label="Find Driver by ID, Name, Surname, or Code"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2" align="right">
            Total: {totalDrivers}
          </Typography>
        </Grid>
      </Grid>

      {/* Second row: Status filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          width: '100%',
          minHeight: '24px', // Ensure consistent height before and after loading
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
          }}
        >
          {allStatuses.map((status) => {
            const count = statusCounts[status] || 0;
            const isLoading = !statusCounts.hasOwnProperty(status);

            return (
              <Box
                key={status}
                onClick={() => onStatusClick(status === selectedStatus ? 'all' : status)}
                sx={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  opacity: selectedStatus === status ? 1 : 0.7,
                  '&:hover': {
                    opacity: 1,
                  },
                  minWidth: '65px', // Reduced from 80px to make it 20% narrower
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mr: 0.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    minWidth: '25px', // Reduced from 30px to make it 20% narrower
                  }}
                >
                  {isLoading ? (
                    <Skeleton width={25} height={16} />
                  ) : (
                    count
                  )}{' '}
                  <DriverStatusChip status={status} />
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Refresh driver list">
            <IconButton
              size="small"
              onClick={onDriversUpdate}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
              aria-label="Refresh driver list"
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Driver status information">
            <IconButton
              size="small"
              onClick={() => setInfoModalOpen(true)}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
              aria-label="Driver status information"
            >
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Info Modal */}
      <Dialog
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Driver Status Information
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Each driver can be in one of the following statuses. Click on a status chip to filter drivers by that status.
          </DialogContentText>
          <List>
            {allStatuses.map((status, index) => {
              const explanation = statusExplanations[status];
              return (
                <React.Fragment key={status}>
                  <ListItem alignItems="flex-start">
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                      <DriverStatusChip status={status} />
                    </Box>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" component="span" fontWeight="bold">
                            {explanation.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span">
                            (Status Code: {explanation.code})
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box component="div" sx={{ mt: 1 }}>
                          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                            <strong>Meaning:</strong> {explanation.meaning}
                          </Typography>
                          <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                            <strong>When it occurs:</strong> {explanation.when}
                          </Typography>
                          <Typography variant="body2" component="div">
                            <strong>Action required:</strong> {explanation.action}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < allStatuses.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DriverListActionBar; 